import { useState, useEffect } from 'react';
import { EnhancedHeader } from '@/components/EnhancedHeader';
import { InteractiveTipCarousel } from '@/components/InteractiveTipCarousel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Zap, Trophy } from 'lucide-react';

export default function InteractiveTips() {
  // Sample data - replace with real data from Supabase
  const [upcomingTips] = useState([
    {
      id: '1',
      sport: 'Football',
      league: 'Premier League',
      match: 'Manchester United vs Liverpool',
      prediction: 'Over 2.5 Goals',
      odds: 1.85,
      confidence: 78,
      trustScore: 82,
      price: 9.99,
      expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      isAI: true,
      rationale: 'Both teams have scored in their last 5 meetings. Manchester United averages 2.1 goals at home while Liverpool concedes an average of 1.4 goals away. Recent form and head-to-head stats strongly favor over 2.5 goals.',
      recentForm: {
        homeTeam: ['W', 'W', 'D', 'W', 'L'],
        awayTeam: ['W', 'L', 'W', 'W', 'D']
      },
      stats: [
        { label: 'Home Goals/Game', value: '2.1' },
        { label: 'Away Goals/Game', value: '1.8' },
        { label: 'H2H Over 2.5', value: '80%' },
        { label: 'Both Teams Score', value: '73%' }
      ]
    },
    {
      id: '2',
      sport: 'Basketball',
      league: 'NBA',
      match: 'Lakers vs Warriors',
      prediction: 'Warriors -3.5',
      odds: 1.91,
      confidence: 72,
      trustScore: 76,
      price: 14.99,
      expiresAt: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
      isAI: true,
      rationale: 'Warriors have won 7 of their last 10 home games against Lakers. Curry is averaging 28.5 points in the last 3 games. Lakers playing back-to-back with key injuries to Davis.',
      recentForm: {
        homeTeam: ['W', 'W', 'W', 'L', 'W'],
        awayTeam: ['L', 'W', 'L', 'L', 'W']
      },
      stats: [
        { label: 'Home Win %', value: '68%' },
        { label: 'Away Loss %', value: '45%' },
        { label: 'Avg Point Diff', value: '+5.2' },
        { label: 'Injury Impact', value: 'High' }
      ]
    },
    {
      id: '3',
      sport: 'Tennis',
      league: 'ATP Masters',
      match: 'Djokovic vs Alcaraz',
      prediction: 'Djokovic to Win',
      odds: 2.10,
      confidence: 65,
      trustScore: 71,
      price: 0,
      expiresAt: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
      isAI: false,
      rationale: 'Djokovic has superior hard court record. Recent 3-set wins show peak form. Alcaraz struggling with consistency in last 2 tournaments.',
      recentForm: {
        homeTeam: ['W', 'W', 'W', 'W', 'L'],
        awayTeam: ['W', 'L', 'L', 'W', 'L']
      },
      stats: [
        { label: 'Hard Court Win %', value: '84%' },
        { label: 'H2H Record', value: '3-2' },
        { label: 'Service Games Won', value: '89%' },
        { label: 'Break Points Saved', value: '67%' }
      ]
    }
  ]);

  return (
    <div className="min-h-screen bg-gradient-dark">
      <EnhancedHeader />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
            Interactive Predictions
          </h1>
          <p className="text-muted-foreground">
            Swipe through upcoming tips with detailed analysis and tap to reveal insights
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Available Tips</p>
                <p className="text-2xl font-bold text-foreground">{upcomingTips.length}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">AI Predictions</p>
                <p className="text-2xl font-bold text-foreground">
                  {upcomingTips.filter(t => t.isAI).length}
                </p>
              </div>
              <Zap className="h-8 w-8 text-premium" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Trust Score</p>
                <p className="text-2xl font-bold text-foreground">
                  {(upcomingTips.reduce((acc, t) => acc + t.trustScore, 0) / upcomingTips.length).toFixed(0)}
                </p>
              </div>
              <Trophy className="h-8 w-8 text-profit" />
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="mb-8">
          <TabsList className="grid w-full grid-cols-4 max-w-md">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="football">Football</TabsTrigger>
            <TabsTrigger value="basketball">Basketball</TabsTrigger>
            <TabsTrigger value="tennis">Tennis</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <InteractiveTipCarousel tips={upcomingTips} />
          </TabsContent>

          <TabsContent value="football" className="mt-6">
            <InteractiveTipCarousel 
              tips={upcomingTips.filter(t => t.sport === 'Football')} 
            />
          </TabsContent>

          <TabsContent value="basketball" className="mt-6">
            <InteractiveTipCarousel 
              tips={upcomingTips.filter(t => t.sport === 'Basketball')} 
            />
          </TabsContent>

          <TabsContent value="tennis" className="mt-6">
            <InteractiveTipCarousel 
              tips={upcomingTips.filter(t => t.sport === 'Tennis')} 
            />
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-premium" />
              How to Use Interactive Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Badge className="mt-0.5">1</Badge>
                <span>Swipe or use arrows to browse through available predictions</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge className="mt-0.5">2</Badge>
                <span>Click "Why this prediction?" to reveal detailed analysis, recent form, and key statistics</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge className="mt-0.5">3</Badge>
                <span>Check the Trust Score badge for model certainty and reliability</span>
              </li>
              <li className="flex items-start gap-2">
                <Badge className="mt-0.5">4</Badge>
                <span>Purchase premium tips or get free predictions with one click</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
